import { useGetDossierDetailsById } from "@/entities/dossier-details";
import { RoleGate, UserRole } from "@/features/auth";
import { BerichtenDetails } from "@/features/berichten/components/berichten-details";
import { BetrokkenStationsDetails } from "@/features/betrokken-stations";
import { BetrokkenTreinenSummary } from "@/features/betrokken-treinen";
import { DossierMain, getReikwijdteLabel, SectionGate } from "@/features/dossier-details";
import { EditDetailsDialog, EditSectiesDialog, LinkToOGDialog } from "@/features/dossier-management";
import { CustomSectionsNotification } from "@/features/dossier-management/components/settings/custom-sections-notification";
import { SectionsProvider } from "@/features/dossier-management/sections-provider";
import { EvaluatieSummary } from "@/features/evaluatie";
import { FaciliteitenSummary, LandelijkeFaciliteitenSummary } from "@/features/faciliteiten";
import { MedewerkersOpStationSummary } from "@/features/medewerkers-op-station";
import { TvvSummary } from "@/features/tvv";
import { VsmOfOpstartplanSummary } from "@/features/vsm-of-opstartplan";
import {
    Button,
    ErrorDisplay,
    Separator,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/shared/components";
import { useArchiveVisibility, useIsScrolled } from "@/shared/hooks";
import { cn } from "@/shared/utils";
import { useState } from "react";
import { useParams } from "react-router";
import { DossierDetailsContent, DossierDetailsPanel } from "./dossier-details-panel";
import { DossierToolbar } from "./dossier-toolbar";

const DOSSIER_TAB_ROLES: UserRole[] = [
    UserRole.REGIEVOERDER,
    UserRole.ADMIN,
    UserRole.KLANTENSERVICE,
    UserRole.SUPPORTCONSULTANT,
    UserRole.ALLEENLEZEN,
];

type DossierTab = "dossier" | "berichten" | "stations";

const tabRoleMap: Record<DossierTab, UserRole[]> = {
    dossier: DOSSIER_TAB_ROLES,
    berichten: DOSSIER_TAB_ROLES.filter((role) => role !== UserRole.KLANTENSERVICE),
    stations: DOSSIER_TAB_ROLES,
};

export default function DossierDetails() {
    const { ref, isScrolled } = useIsScrolled();
    const { id: dossierNummer } = useParams() as { id: string };
    const isArchived = useArchiveVisibility();
    const { data: dossierDetails, error } = useGetDossierDetailsById(dossierNummer);
    const [activeTab, setActiveTab] = useState<DossierTab>("dossier");
    const [editDetailsDialogOpen, setEditDetailsDialogOpen] = useState(false);
    const [editSectiesDialogOpen, setEditSectiesDialogOpen] = useState(false);
    const [linkToSpoorwebDialogOpen, setLinkToSpoorwebDialogOpen] = useState(false);

    if (error) {
        return (
            <ErrorDisplay
                error={error}
                variant="section"
                title="Fout bij het laden van het dossier"
                onRetry={() => window.location.reload()}
            />
        );
    }

    if (!dossierDetails) {
        return null;
    }

    return (
        <DossierDetailsPanel>
            <SectionsProvider dossierId={dossierNummer}>
                <Tabs
                    className="bg-elevated-1 z-10 grid gap-0 overflow-hidden"
                    value={activeTab}
                    onValueChange={(value) => setActiveTab(value as DossierTab)}
                >
                    <hgroup
                        className={cn(
                            "text-content-emphasis bg-elevated-1 sticky top-0 z-10 grid gap-0 px-0 pt-3 font-medium",
                            isScrolled.top && "shadow-elevated-2"
                        )}
                    >
                        <div className="flex items-center justify-between gap-4 px-4">
                            <h1 className="text-xxl flex gap-2 font-normal" data-testid="dossier-omschrijving">
                                {dossierDetails.omschrijving}
                                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                data-testid="edit-details"
                                                variant="tertiary"
                                                icon="edit"
                                                iconOnly
                                                iconSize="xs"
                                                onClick={() => setEditDetailsDialogOpen(true)}
                                            />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-200">Wijzig details</TooltipContent>
                                    </Tooltip>
                                    {!dossierDetails.spoorweb && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    data-testid="link-og"
                                                    variant="tertiary"
                                                    icon="connect"
                                                    iconOnly
                                                    iconSize="xs"
                                                    onClick={() => setLinkToSpoorwebDialogOpen(true)}
                                                />
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-200">Spoorweb koppelen aan dossier</TooltipContent>
                                        </Tooltip>
                                    )}
                                    {editDetailsDialogOpen && (
                                        <EditDetailsDialog
                                            dossierId={dossierNummer}
                                            omschrijving={dossierDetails.omschrijving}
                                            omschrijvingExtraContext={dossierDetails.omschrijvingExtraContext}
                                            reikwijdte={dossierDetails.reikwijdte}
                                            trajecten={dossierDetails.trajecten}
                                            stations={dossierDetails.stations}
                                            open={editDetailsDialogOpen}
                                            onOpenChange={setEditDetailsDialogOpen}
                                        />
                                    )}
                                    {linkToSpoorwebDialogOpen && (
                                        <LinkToOGDialog
                                            dossierId={dossierNummer}
                                            open={linkToSpoorwebDialogOpen}
                                            onOpenChange={setLinkToSpoorwebDialogOpen}
                                        />
                                    )}
                                </RoleGate>
                            </h1>
                            <DossierToolbar dossierNummer={dossierNummer}>
                                <DossierToolbar.Pin />
                                <DossierToolbar.BeeindigDossier />
                            </DossierToolbar>
                        </div>
                        <p className="text-l text-content-subtle flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pb-2">
                            <span
                                className="text-s leading-l text-content-default font-medium"
                                data-testid="dossier-reikwijdte"
                                data-reikwijdte={dossierDetails.reikwijdte}
                            >
                                {getReikwijdteLabel(dossierDetails.reikwijdte, dossierDetails.stations, dossierDetails.trajecten)}
                            </span>
                            {dossierDetails.omschrijvingExtraContext && (
                                <span className="text-s leading-m font-normal" data-testid="dossier-omschrijving-extra-context">
                                    {dossierDetails.omschrijvingExtraContext}
                                </span>
                            )}
                        </p>
                        <RoleGate roles={DOSSIER_TAB_ROLES}>
                            <TabsList className="px-4">
                                <RoleGate roles={tabRoleMap["dossier"]}>
                                    <TabsTrigger value="dossier">Dossier</TabsTrigger>
                                </RoleGate>
                                <RoleGate roles={tabRoleMap["berichten"]}>
                                    <TabsTrigger value="berichten">Berichten</TabsTrigger>
                                </RoleGate>
                                <RoleGate roles={tabRoleMap["stations"]}>
                                    <TabsTrigger value="stations">Stations</TabsTrigger>
                                </RoleGate>
                            </TabsList>
                        </RoleGate>
                        <RoleGate roles={[UserRole.TRANSVISION, UserRole.DEWIT, UserRole.VRIJWILLIGER]}>
                            <Separator orientation="horizontal" />
                        </RoleGate>
                    </hgroup>
                    <TabsContent value="dossier" className="overflow-y-auto" ref={ref}>
                        <DossierDetailsContent className="flex flex-col gap-2 pt-3">
                            <SectionGate section="DossierDetails">
                                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                    {!isArchived && (
                                        <CustomSectionsNotification
                                            dossierId={dossierNummer}
                                            onEditSections={() => setEditSectiesDialogOpen(true)}
                                        />
                                    )}
                                </RoleGate>
                                <DossierMain dossierId={dossierNummer} dossierDetails={dossierDetails} />
                            </SectionGate>
                            <SectionGate section="VsmOfOpstartplan">
                                <VsmOfOpstartplanSummary dossierNummer={dossierNummer} />
                            </SectionGate>
                            <SectionGate section="Tvv">
                                <TvvSummary dossierNummer={dossierNummer} />
                            </SectionGate>
                            <SectionGate section="Faciliteiten">
                                <FaciliteitenSummary dossierNummer={dossierNummer} />
                            </SectionGate>
                            <SectionGate section="FaciliteitenLandelijk">
                                <LandelijkeFaciliteitenSummary dossierNummer={dossierNummer} />
                            </SectionGate>
                            <SectionGate section="BetrokkenTreinen">
                                <BetrokkenTreinenSummary dossierNummer={dossierNummer} />
                            </SectionGate>
                            <SectionGate section="MedewerkersOpStation">
                                <MedewerkersOpStationSummary dossierNummer={dossierNummer} />{" "}
                            </SectionGate>
                            <SectionGate section="Evaluatie">
                                <EvaluatieSummary dossierNummer={dossierNummer} />
                            </SectionGate>

                            <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                <Button
                                    variant="tertiary"
                                    icon="settings"
                                    iconSize="xs"
                                    onClick={() => setEditSectiesDialogOpen(true)}
                                    className="m-auto"
                                >
                                    Secties instellen
                                </Button>
                            </RoleGate>
                            {editSectiesDialogOpen && (
                                <EditSectiesDialog
                                    dossierId={dossierNummer}
                                    open={editSectiesDialogOpen}
                                    onOpenChange={setEditSectiesDialogOpen}
                                />
                            )}
                        </DossierDetailsContent>
                    </TabsContent>
                    <RoleGate roles={tabRoleMap["berichten"]}>
                        <TabsContent value="berichten" className="overflow-y-auto">
                            <BerichtenDetails />
                        </TabsContent>
                    </RoleGate>
                    <RoleGate roles={tabRoleMap["stations"]}>
                        <TabsContent value="stations" className="overflow-y-auto">
                            <BetrokkenStationsDetails />
                        </TabsContent>
                    </RoleGate>
                </Tabs>
            </SectionsProvider>
        </DossierDetailsPanel>
    );
}
